import { useTranslation } from "react-i18next";

interface SkillProductCardProps {
  product: {
    id: string;
    name: string;
    price: number;
    imageUrl: string;
    stockQuantity: number;
  };
  selectedQuantity?: number;
  onAdd?: () => void;
  onQuantityChange?: (quantity: number) => void;
}

export default function SkillProductCard({
  product,
  selectedQuantity = 0,
  onAdd,
  onQuantityChange,
}: SkillProductCardProps) {
  const { t } = useTranslation("common");
  const isSelected = selectedQuantity > 0;
  const isOutOfStock = product.stockQuantity <= 0;

  return (
    <div
      className={`rounded-2xl border p-3 transition-all w-full max-w-full ${
        isSelected
          ? "bg-primary-50/40 border-primary-200 shadow-sm"
          : "bg-white border-gray-100 hover:border-primary-100 hover:shadow-sm"
      }`}
    >
      <div className="grid grid-cols-[86px_minmax(0,1fr)] sm:grid-cols-[104px_minmax(0,1fr)] gap-3 items-stretch">
        <div className="w-[86px] h-[104px] sm:w-[104px] sm:h-[124px] rounded-xl overflow-hidden bg-gray-50 border border-gray-100">
          <img
            src={product.imageUrl}
            alt={product.name}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        </div>

        <div className="flex-1 min-w-0 flex flex-col justify-between gap-3">
          <div>
            <h4 className="text-sm font-bold text-navy-400 line-clamp-2 leading-relaxed">
              {product.name}
            </h4>
            <div className="mt-1 flex flex-wrap items-center gap-2">
              <span className="text-sm font-bold text-primary-400">
                {product.price} {t("sar")}
              </span>
              <span className="h-1 w-1 rounded-full bg-gray-300" />
              <span className="text-[11px] text-gray-500">
                {t("productAvailable")}: {product.stockQuantity}
              </span>
              {isSelected && (
                <span className="rounded-full bg-primary-100 px-2 py-0.5 text-[10px] font-semibold text-primary-500">
                  {t("productInCart")}
                </span>
              )}
            </div>
            {isOutOfStock && (
              <p className="mt-2 text-[11px] font-semibold text-red-500">
                {t("productOutOfStock")}
              </p>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {isSelected ? (
              <>
                <div className="h-10 rounded-xl border border-gray-200 bg-white flex items-center overflow-hidden">
                  <button
                    type="button"
                    onClick={() => onQuantityChange?.(selectedQuantity - 1)}
                    className="w-9 h-full text-navy-400 flex items-center justify-center hover:bg-gray-50"
                    aria-label={t("decreaseQuantity")}
                    title={t("decreaseQuantity")}
                  >
                    <i className="ri-subtract-line text-sm" />
                  </button>
                  <input
                    type="number"
                    min={0}
                    max={product.stockQuantity}
                    value={selectedQuantity}
                    onChange={(event) => onQuantityChange?.(Number(event.target.value))}
                    className="w-12 h-full border-x border-gray-100 text-center text-sm font-bold text-navy-400 outline-none"
                    aria-label={t("productQuantity")}
                  />
                  <button
                    type="button"
                    onClick={() => onQuantityChange?.(selectedQuantity + 1)}
                    disabled={selectedQuantity >= product.stockQuantity}
                    className="w-9 h-full text-navy-400 flex items-center justify-center hover:bg-gray-50 disabled:opacity-35"
                    aria-label={t("increaseQuantity")}
                    title={t("increaseQuantity")}
                  >
                    <i className="ri-add-line text-sm" />
                  </button>
                </div>
                <button
                  type="button"
                  onClick={() => onQuantityChange?.(0)}
                  className="h-10 w-10 rounded-xl border border-red-100 bg-white text-red-500 flex items-center justify-center hover:bg-red-50"
                  aria-label={t("removeProductFromCart")}
                  title={t("removeProductFromCart")}
                >
                  <i className="ri-delete-bin-line text-sm" />
                </button>
              </>
            ) : (
              <button
                type="button"
                onClick={onAdd}
                disabled={isOutOfStock}
                className="h-10 w-10 rounded-xl bg-primary-400 hover:bg-primary-500 disabled:bg-gray-200 disabled:text-gray-400 text-white flex items-center justify-center transition-colors"
                aria-label={t("addProductToCart")}
                title={t("addProductToCart")}
              >
                <i className="ri-shopping-cart-line text-lg" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
